import {
  useFileCollectionFromServiceId,
  useScanService,
} from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { showErrorToaster } from '@/utils/toasters';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import { downloadFileCollection } from '@/utils/files';
import { Tab } from '@/components/TabsBlock';
import DownloadToaster from '@/toasters/DownloadToaster';
import BridgeFilesDialog from '@/dialogs/BridgeFilesDialog';
import Button from '@/components/Button';
import { DownloadIcon, OrderIcon } from '@/icons';
import { classes } from './utils';

export type OverviewTabProps = {
  name: string;
  label: string;
};

export default function ExportsTab({ name, label }: OverviewTabProps) {
  const { activeServiceId, onApiError } = useAppState();
  const scanService = useScanService(activeServiceId);
  const fileCollection = useFileCollectionFromServiceId(activeServiceId);
  const [downloadMessage, setDownloadMessage] = useString('Fetching files...');
  const [isPrinting, togglePrinting] = useToggle();
  const [isPreparingDownload, togglePreparingDownload] = useToggle();
  const [isDownloadToasterOpen, toggleDownloadToaster] = useToggle();

  const handleDownload = async () => {
    try {
      if (!fileCollection) return showErrorToaster('Error downloading files');
      togglePreparingDownload(true);
      toggleDownloadToaster(true);
      await downloadFileCollection(fileCollection.id, setDownloadMessage);
      togglePreparingDownload(false);
    } catch (err) {
      handleCloseDownloadToaster();
      onApiError(err, 'Error downloading collection');
    }
  };

  const handleCloseDownloadToaster = () => {
    togglePreparingDownload(false);
    toggleDownloadToaster(false);
  };

  if (!scanService) return null;

  return (
    <>
      <DownloadToaster
        open={isDownloadToasterOpen}
        isLoading={isPreparingDownload}
        message={downloadMessage}
        onClose={handleCloseDownloadToaster}
      />
      {isPrinting && fileCollection?.id && (
        <BridgeFilesDialog
          fileCollectionId={fileCollection?.id}
          onClose={() => togglePrinting(false)}
        />
      )}
      <Tab className={classes.tab} name={name} label={label}>
        <Button className={classes.button} onClick={handleDownload}>
          <DownloadIcon />
          Download
        </Button>
        <Button disabled className={classes.button} onClick={() => null}>
          <OrderIcon />
          Create Lab Order
        </Button>
      </Tab>
    </>
  );
}
